import { useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { selectUser } from "../redux/slices/authSlice";
import axiosInstance from "../redux/services/axios";
import { toast } from "sonner";

const FEEDBACK_KEY = "feedback_submitted";
const REMIND_LATER_KEY = "feedback_remind_later_until";
const FIVE_MINUTES = 5 * 60 * 1000;
const TWO_DAYS = 2 * 24 * 60 * 60 * 1000;

export const useFeedbackModal = (tourDone = true) => {
    const [isOpen, setIsOpen] = useState(false);
    const user = useSelector(selectUser);
    const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

    useEffect(() => {
        const shouldShowFeedback = user?.is_feedback_popup ?? false;
        const hasSubmitted = localStorage.getItem(FEEDBACK_KEY);

        const remindUntil = localStorage.getItem(REMIND_LATER_KEY);
        const isSnoozed = remindUntil && Date.now() < Number(remindUntil);

        if (!tourDone || !shouldShowFeedback || hasSubmitted || isSnoozed) {
            return;
        }

        // Show immediately on mount
        setIsOpen(true);

        // Re-show every 5 minutes until the user submits
        intervalRef.current = setInterval(() => {
            if (localStorage.getItem(FEEDBACK_KEY)) {
                clearInterval(intervalRef.current!);
                return;
            }
            setIsOpen(true);
        }, FIVE_MINUTES);

        return () => {
            if (intervalRef.current) clearInterval(intervalRef.current);
        };
    }, [user?.is_feedback_popup, tourDone]);

    const handleClose = () => setIsOpen(false);

    const handleRemindLater = async () => {
        // Suppress modal locally for 2 days so navigation doesn't re-trigger it
        localStorage.setItem(REMIND_LATER_KEY, String(Date.now() + TWO_DAYS));
        if (intervalRef.current) clearInterval(intervalRef.current);
        setIsOpen(false);
        try {
            await axiosInstance.post(`${process.env.REACT_APP_BACKEND_URL}/feedback/remind-later`);
        } catch {
            // silently fail
        }
    };

    const handleSubmit = async (star_rating: number, description: string) => {
        try {
            await axiosInstance.post(`${process.env.REACT_APP_BACKEND_URL}/feedback/feedback`, {
                star_rating,
                description,
            });
            //   await axiosInstance.post('https://backend-api-pmhg.onrender.com/api/v1.0.1/feedback/feedback', {
            //     star_rating,
            //     description,
            // });
            localStorage.setItem(FEEDBACK_KEY, 'true');
            localStorage.removeItem(REMIND_LATER_KEY);
            if (intervalRef.current) clearInterval(intervalRef.current);
            setIsOpen(false);
            toast.success('Feedback submitted successfully!');
            return true;
        } catch (error: any) {
            const message = error?.response?.data?.message || error?.message || 'Failed to submit feedback. Please try again.';
            toast.error(message);
            return false;
        }
    };

    return { isOpen, handleClose, handleRemindLater, handleSubmit };
};